/**
 * Generated by @openapi-codegen
 *
 * @version v1
 */
import * as reactQuery from 'react-query';
import { useOrgsContext, OrgsContext } from './orgsContext';
import type * as Fetcher from './orgsFetcher';
import { orgsFetch } from './orgsFetcher';
import type * as Schemas from './orgsSchemas';

export type GetOrganizationsPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
};

export type GetOrganizationsQueryParams = {
  /*
   * search by name
   */
  search?: string;
  /*
   * @format int32
   */
  first?: number;
  /*
   * @format int32
   */
  max?: number;
  /*
   * search by attributes using the format `k1:v1,k2:v2`
   */
  q?: string;
};

export type GetOrganizationsError = Fetcher.ErrorWrapper<undefined>;

export type GetOrganizationsResponse = Schemas.OrganizationRepresentation[];

export type GetOrganizationsVariables = {
  pathParams: GetOrganizationsPathParams;
  queryParams?: GetOrganizationsQueryParams;
} & OrgsContext['fetcherOptions'];

/**
 * Get a paginated list of organizations using optional search query parameters.
 */
export const fetchGetOrganizations = (variables: GetOrganizationsVariables) =>
  orgsFetch<
    GetOrganizationsResponse,
    GetOrganizationsError,
    undefined,
    {},
    GetOrganizationsQueryParams,
    GetOrganizationsPathParams
  >({ url: '/{realm}/orgs', method: 'get', ...variables });

/**
 * Get a paginated list of organizations using optional search query parameters.
 */
export const useGetOrganizations = (
  variables: GetOrganizationsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetOrganizationsResponse, GetOrganizationsError, GetOrganizationsResponse>,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<GetOrganizationsResponse, GetOrganizationsError, GetOrganizationsResponse>(
    queryKeyFn({ path: '/{realm}/orgs', operationId: 'getOrganizations', variables }),
    () => fetchGetOrganizations({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type CreateOrganizationPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
};

export type CreateOrganizationError = Fetcher.ErrorWrapper<undefined>;

export type CreateOrganizationVariables = {
  body?: Schemas.OrganizationRepresentation;
  pathParams: CreateOrganizationPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchCreateOrganization = (variables: CreateOrganizationVariables) =>
  orgsFetch<
    undefined,
    CreateOrganizationError,
    Schemas.OrganizationRepresentation,
    {},
    {},
    CreateOrganizationPathParams
  >({ url: '/{realm}/orgs', method: 'post', ...variables });

export const useCreateOrganization = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, CreateOrganizationError, CreateOrganizationVariables>,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, CreateOrganizationError, CreateOrganizationVariables>(
    (variables: CreateOrganizationVariables) => fetchCreateOrganization({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetOrganizationsCountPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
};

export type GetOrganizationsCountQueryParams = {
  search?: string;
};

export type GetOrganizationsCountError = Fetcher.ErrorWrapper<undefined>;

export type GetOrganizationsCountVariables = {
  pathParams: GetOrganizationsCountPathParams;
  queryParams?: GetOrganizationsCountQueryParams;
} & OrgsContext['fetcherOptions'];

/**
 * Get a count of organizations using an optional search query.
 */
export const fetchGetOrganizationsCount = (variables: GetOrganizationsCountVariables) =>
  orgsFetch<
    number,
    GetOrganizationsCountError,
    undefined,
    {},
    GetOrganizationsCountQueryParams,
    GetOrganizationsCountPathParams
  >({ url: '/{realm}/orgs/count', method: 'get', ...variables });

/**
 * Get a count of organizations using an optional search query.
 */
export const useGetOrganizationsCount = (
  variables: GetOrganizationsCountVariables,
  options?: Omit<reactQuery.UseQueryOptions<number, GetOrganizationsCountError, number>, 'queryKey' | 'queryFn'>
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<number, GetOrganizationsCountError, number>(
    queryKeyFn({ path: '/{realm}/orgs/count', operationId: 'getOrganizationsCount', variables }),
    () => fetchGetOrganizationsCount({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetMePathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
};

export type GetMeError = Fetcher.ErrorWrapper<undefined>;

export type GetMeVariables = {
  pathParams: GetMePathParams;
} & OrgsContext['fetcherOptions'];

/**
 * Get a list of all organizations that the user is a member and their roles in those organizations. Similar idea to /userinfo in OIDC.
 */
export const fetchGetMe = (variables: GetMeVariables) =>
  orgsFetch<Schemas.MyOrganizationsRepresentation, GetMeError, undefined, {}, {}, GetMePathParams>({
    url: '/{realm}/orgs/me',
    method: 'get',
    ...variables,
  });

/**
 * Get a list of all organizations that the user is a member and their roles in those organizations. Similar idea to /userinfo in OIDC.
 */
export const useGetMe = (
  variables: GetMeVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.MyOrganizationsRepresentation,
      GetMeError,
      Schemas.MyOrganizationsRepresentation
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<Schemas.MyOrganizationsRepresentation, GetMeError, Schemas.MyOrganizationsRepresentation>(
    queryKeyFn({ path: '/{realm}/orgs/me', operationId: 'getMe', variables }),
    () => fetchGetMe({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetOrganizationByIdPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
};

export type GetOrganizationByIdError = Fetcher.ErrorWrapper<undefined>;

export type GetOrganizationByIdVariables = {
  pathParams: GetOrganizationByIdPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchGetOrganizationById = (variables: GetOrganizationByIdVariables) =>
  orgsFetch<
    Schemas.OrganizationRepresentation,
    GetOrganizationByIdError,
    undefined,
    {},
    {},
    GetOrganizationByIdPathParams
  >({ url: '/{realm}/orgs/{orgId}', method: 'get', ...variables });

export const useGetOrganizationById = (
  variables: GetOrganizationByIdVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.OrganizationRepresentation,
      GetOrganizationByIdError,
      Schemas.OrganizationRepresentation
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<
    Schemas.OrganizationRepresentation,
    GetOrganizationByIdError,
    Schemas.OrganizationRepresentation
  >(
    queryKeyFn({ path: '/{realm}/orgs/{orgId}', operationId: 'getOrganizationById', variables }),
    () => fetchGetOrganizationById({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type UpdateOrganizationPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
};

export type UpdateOrganizationError = Fetcher.ErrorWrapper<undefined>;

export type UpdateOrganizationVariables = {
  body?: Schemas.OrganizationRepresentation;
  pathParams: UpdateOrganizationPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchUpdateOrganization = (variables: UpdateOrganizationVariables) =>
  orgsFetch<
    undefined,
    UpdateOrganizationError,
    Schemas.OrganizationRepresentation,
    {},
    {},
    UpdateOrganizationPathParams
  >({ url: '/{realm}/orgs/{orgId}', method: 'put', ...variables });

export const useUpdateOrganization = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, UpdateOrganizationError, UpdateOrganizationVariables>,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, UpdateOrganizationError, UpdateOrganizationVariables>(
    (variables: UpdateOrganizationVariables) => fetchUpdateOrganization({ ...fetcherOptions, ...variables }),
    options
  );
};

export type DeleteOrganizationPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
};

export type DeleteOrganizationError = Fetcher.ErrorWrapper<undefined>;

export type DeleteOrganizationVariables = {
  pathParams: DeleteOrganizationPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchDeleteOrganization = (variables: DeleteOrganizationVariables) =>
  orgsFetch<undefined, DeleteOrganizationError, undefined, {}, {}, DeleteOrganizationPathParams>({
    url: '/{realm}/orgs/{orgId}',
    method: 'delete',
    ...variables,
  });

export const useDeleteOrganization = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, DeleteOrganizationError, DeleteOrganizationVariables>,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, DeleteOrganizationError, DeleteOrganizationVariables>(
    (variables: DeleteOrganizationVariables) => fetchDeleteOrganization({ ...fetcherOptions, ...variables }),
    options
  );
};

export type CreatePortalLinkPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
};

export type CreatePortalLinkError = Fetcher.ErrorWrapper<undefined>;

export type CreatePortalLinkVariables = {
  pathParams: CreatePortalLinkPathParams;
} & OrgsContext['fetcherOptions'];

/**
 * Create a link for this organizations admin portal. This link encodes an action token on behalf of the organization's default admin user, or the user that is optionally specified in this request. The user specified must be a member of this organization, and have full organization admin roles.
 */
export const fetchCreatePortalLink = (variables: CreatePortalLinkVariables) =>
  orgsFetch<Schemas.PortalLinkRepresentation, CreatePortalLinkError, undefined, {}, {}, CreatePortalLinkPathParams>({
    url: '/{realm}/orgs/{orgId}/portal-link',
    method: 'post',
    ...variables,
  });

/**
 * Create a link for this organizations admin portal. This link encodes an action token on behalf of the organization's default admin user, or the user that is optionally specified in this request. The user specified must be a member of this organization, and have full organization admin roles.
 */
export const useCreatePortalLink = (
  options?: Omit<
    reactQuery.UseMutationOptions<Schemas.PortalLinkRepresentation, CreatePortalLinkError, CreatePortalLinkVariables>,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<Schemas.PortalLinkRepresentation, CreatePortalLinkError, CreatePortalLinkVariables>(
    (variables: CreatePortalLinkVariables) => fetchCreatePortalLink({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetOrganizationMembershipsPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
};

export type GetOrganizationMembershipsQueryParams = {
  search?: string;
  /*
   * @format int32
   */
  first?: number;
  /*
   * @format int32
   */
  max?: number;
};

export type GetOrganizationMembershipsError = Fetcher.ErrorWrapper<undefined>;

export type GetOrganizationMembershipsResponse = Schemas.UserRepresentation[];

export type GetOrganizationMembershipsVariables = {
  pathParams: GetOrganizationMembershipsPathParams;
  queryParams?: GetOrganizationMembershipsQueryParams;
} & OrgsContext['fetcherOptions'];

/**
 * Get a paginated list of users who are a member of the specified organization.
 */
export const fetchGetOrganizationMemberships = (variables: GetOrganizationMembershipsVariables) =>
  orgsFetch<
    GetOrganizationMembershipsResponse,
    GetOrganizationMembershipsError,
    undefined,
    {},
    GetOrganizationMembershipsQueryParams,
    GetOrganizationMembershipsPathParams
  >({ url: '/{realm}/orgs/{orgId}/members', method: 'get', ...variables });

/**
 * Get a paginated list of users who are a member of the specified organization.
 */
export const useGetOrganizationMemberships = (
  variables: GetOrganizationMembershipsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetOrganizationMembershipsResponse,
      GetOrganizationMembershipsError,
      GetOrganizationMembershipsResponse
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<
    GetOrganizationMembershipsResponse,
    GetOrganizationMembershipsError,
    GetOrganizationMembershipsResponse
  >(
    queryKeyFn({ path: '/{realm}/orgs/{orgId}/members', operationId: 'getOrganizationMemberships', variables }),
    () => fetchGetOrganizationMemberships({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetOrganizationMembershipsCountPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
};

export type GetOrganizationMembershipsCountError = Fetcher.ErrorWrapper<undefined>;

export type GetOrganizationMembershipsCountVariables = {
  pathParams: GetOrganizationMembershipsCountPathParams;
} & OrgsContext['fetcherOptions'];

/**
 * Get total number of members of a given organization
 */
export const fetchGetOrganizationMembershipsCount = (variables: GetOrganizationMembershipsCountVariables) =>
  orgsFetch<number, GetOrganizationMembershipsCountError, undefined, {}, {}, GetOrganizationMembershipsCountPathParams>(
    { url: '/{realm}/orgs/{orgId}/members/count', method: 'get', ...variables }
  );

/**
 * Get total number of members of a given organization
 */
export const useGetOrganizationMembershipsCount = (
  variables: GetOrganizationMembershipsCountVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<number, GetOrganizationMembershipsCountError, number>,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<number, GetOrganizationMembershipsCountError, number>(
    queryKeyFn({
      path: '/{realm}/orgs/{orgId}/members/count',
      operationId: 'getOrganizationMembershipsCount',
      variables,
    }),
    () => fetchGetOrganizationMembershipsCount({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetOrganizationDomainsPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
};

export type GetOrganizationDomainsError = Fetcher.ErrorWrapper<undefined>;

export type GetOrganizationDomainsResponse = Schemas.OrganizationDomainRepresentation[];

export type GetOrganizationDomainsVariables = {
  pathParams: GetOrganizationDomainsPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchGetOrganizationDomains = (variables: GetOrganizationDomainsVariables) =>
  orgsFetch<
    GetOrganizationDomainsResponse,
    GetOrganizationDomainsError,
    undefined,
    {},
    {},
    GetOrganizationDomainsPathParams
  >({ url: '/{realm}/orgs/{orgId}/domains', method: 'get', ...variables });

export const useGetOrganizationDomains = (
  variables: GetOrganizationDomainsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetOrganizationDomainsResponse,
      GetOrganizationDomainsError,
      GetOrganizationDomainsResponse
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<
    GetOrganizationDomainsResponse,
    GetOrganizationDomainsError,
    GetOrganizationDomainsResponse
  >(
    queryKeyFn({ path: '/{realm}/orgs/{orgId}/domains', operationId: 'getOrganizationDomains', variables }),
    () => fetchGetOrganizationDomains({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GetOrganizationDomainPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  /*
   * domain name
   */
  domainName: string;
};

export type GetOrganizationDomainError = Fetcher.ErrorWrapper<undefined>;

export type GetOrganizationDomainVariables = {
  pathParams: GetOrganizationDomainPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchGetOrganizationDomain = (variables: GetOrganizationDomainVariables) =>
  orgsFetch<
    Schemas.OrganizationDomainRepresentation,
    GetOrganizationDomainError,
    undefined,
    {},
    {},
    GetOrganizationDomainPathParams
  >({ url: '/{realm}/orgs/{orgId}/domains/{domainName}', method: 'get', ...variables });

export const useGetOrganizationDomain = (
  variables: GetOrganizationDomainVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.OrganizationDomainRepresentation,
      GetOrganizationDomainError,
      Schemas.OrganizationDomainRepresentation
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<
    Schemas.OrganizationDomainRepresentation,
    GetOrganizationDomainError,
    Schemas.OrganizationDomainRepresentation
  >(
    queryKeyFn({ path: '/{realm}/orgs/{orgId}/domains/{domainName}', operationId: 'getOrganizationDomain', variables }),
    () => fetchGetOrganizationDomain({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type VerifyDomainPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  /*
   * domain name
   */
  domainName: string;
};

export type VerifyDomainError = Fetcher.ErrorWrapper<undefined>;

export type VerifyDomainVariables = {
  pathParams: VerifyDomainPathParams;
} & OrgsContext['fetcherOptions'];

/**
 * Initiate a verification check for the domain name owned by this organization
 */
export const fetchVerifyDomain = (variables: VerifyDomainVariables) =>
  orgsFetch<undefined, VerifyDomainError, undefined, {}, {}, VerifyDomainPathParams>({
    url: '/{realm}/orgs/{orgId}/domains/{domainName}/verify',
    method: 'post',
    ...variables,
  });

/**
 * Initiate a verification check for the domain name owned by this organization
 */
export const useVerifyDomain = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, VerifyDomainError, VerifyDomainVariables>, 'mutationFn'>
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, VerifyDomainError, VerifyDomainVariables>(
    (variables: VerifyDomainVariables) => fetchVerifyDomain({ ...fetcherOptions, ...variables }),
    options
  );
};

export type CheckOrganizationMembershipPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  /*
   * user id
   */
  userId: string;
};

export type CheckOrganizationMembershipError = Fetcher.ErrorWrapper<undefined>;

export type CheckOrganizationMembershipVariables = {
  pathParams: CheckOrganizationMembershipPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchCheckOrganizationMembership = (variables: CheckOrganizationMembershipVariables) =>
  orgsFetch<undefined, CheckOrganizationMembershipError, undefined, {}, {}, CheckOrganizationMembershipPathParams>({
    url: '/{realm}/orgs/{orgId}/members/{userId}',
    method: 'get',
    ...variables,
  });

export const useCheckOrganizationMembership = (
  variables: CheckOrganizationMembershipVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<undefined, CheckOrganizationMembershipError, undefined>,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<undefined, CheckOrganizationMembershipError, undefined>(
    queryKeyFn({
      path: '/{realm}/orgs/{orgId}/members/{userId}',
      operationId: 'checkOrganizationMembership',
      variables,
    }),
    () => fetchCheckOrganizationMembership({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type AddOrganizationMemberPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  /*
   * user id
   */
  userId: string;
};

export type AddOrganizationMemberError = Fetcher.ErrorWrapper<undefined>;

export type AddOrganizationMemberVariables = {
  pathParams: AddOrganizationMemberPathParams;
} & OrgsContext['fetcherOptions'];

/**
 * Add the specified user to the specified organization as a member
 */
export const fetchAddOrganizationMember = (variables: AddOrganizationMemberVariables) =>
  orgsFetch<undefined, AddOrganizationMemberError, undefined, {}, {}, AddOrganizationMemberPathParams>({
    url: '/{realm}/orgs/{orgId}/members/{userId}',
    method: 'put',
    ...variables,
  });

/**
 * Add the specified user to the specified organization as a member
 */
export const useAddOrganizationMember = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, AddOrganizationMemberError, AddOrganizationMemberVariables>,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, AddOrganizationMemberError, AddOrganizationMemberVariables>(
    (variables: AddOrganizationMemberVariables) => fetchAddOrganizationMember({ ...fetcherOptions, ...variables }),
    options
  );
};

export type RemoveOrganizationMemberPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  /*
   * user id
   */
  userId: string;
};

export type RemoveOrganizationMemberError = Fetcher.ErrorWrapper<undefined>;

export type RemoveOrganizationMemberVariables = {
  pathParams: RemoveOrganizationMemberPathParams;
} & OrgsContext['fetcherOptions'];

/**
 * Remove the specified user from the specified organization as a member
 */
export const fetchRemoveOrganizationMember = (variables: RemoveOrganizationMemberVariables) =>
  orgsFetch<undefined, RemoveOrganizationMemberError, undefined, {}, {}, RemoveOrganizationMemberPathParams>({
    url: '/{realm}/orgs/{orgId}/members/{userId}',
    method: 'delete',
    ...variables,
  });

/**
 * Remove the specified user from the specified organization as a member
 */
export const useRemoveOrganizationMember = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, RemoveOrganizationMemberError, RemoveOrganizationMemberVariables>,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, RemoveOrganizationMemberError, RemoveOrganizationMemberVariables>(
    (variables: RemoveOrganizationMemberVariables) =>
      fetchRemoveOrganizationMember({ ...fetcherOptions, ...variables }),
    options
  );
};

export type AddOrganizationInvitationPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
};

export type AddOrganizationInvitationError = Fetcher.ErrorWrapper<undefined>;

export type AddOrganizationInvitationVariables = {
  body?: Schemas.InvitationRequestRepresentation;
  pathParams: AddOrganizationInvitationPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchAddOrganizationInvitation = (variables: AddOrganizationInvitationVariables) =>
  orgsFetch<
    undefined,
    AddOrganizationInvitationError,
    Schemas.InvitationRequestRepresentation,
    {},
    {},
    AddOrganizationInvitationPathParams
  >({ url: '/{realm}/orgs/{orgId}/invitations', method: 'post', ...variables });

export const useAddOrganizationInvitation = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, AddOrganizationInvitationError, AddOrganizationInvitationVariables>,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, AddOrganizationInvitationError, AddOrganizationInvitationVariables>(
    (variables: AddOrganizationInvitationVariables) =>
      fetchAddOrganizationInvitation({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetOrganizationInvitationsPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
};

export type GetOrganizationInvitationsQueryParams = {
  search?: string;
  /*
   * @format int32
   */
  first?: number;
  /*
   * @format int32
   */
  max?: number;
};

export type GetOrganizationInvitationsError = Fetcher.ErrorWrapper<undefined>;

export type GetOrganizationInvitationsResponse = Schemas.InvitationRepresentation[];

export type GetOrganizationInvitationsVariables = {
  pathParams: GetOrganizationInvitationsPathParams;
  queryParams?: GetOrganizationInvitationsQueryParams;
} & OrgsContext['fetcherOptions'];

/**
 * Get a paginated list of invitations to an organization, using an optional search query for email address.
 */
export const fetchGetOrganizationInvitations = (variables: GetOrganizationInvitationsVariables) =>
  orgsFetch<
    GetOrganizationInvitationsResponse,
    GetOrganizationInvitationsError,
    undefined,
    {},
    GetOrganizationInvitationsQueryParams,
    GetOrganizationInvitationsPathParams
  >({ url: '/{realm}/orgs/{orgId}/invitations', method: 'get', ...variables });

/**
 * Get a paginated list of invitations to an organization, using an optional search query for email address.
 */
export const useGetOrganizationInvitations = (
  variables: GetOrganizationInvitationsVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetOrganizationInvitationsResponse,
      GetOrganizationInvitationsError,
      GetOrganizationInvitationsResponse
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<
    GetOrganizationInvitationsResponse,
    GetOrganizationInvitationsError,
    GetOrganizationInvitationsResponse
  >(
    queryKeyFn({ path: '/{realm}/orgs/{orgId}/invitations', operationId: 'getOrganizationInvitations', variables }),
    () => fetchGetOrganizationInvitations({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type RemoveOrganizationInvitationPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  /*
   * invitation id
   */
  invitationId: string;
};

export type RemoveOrganizationInvitationError = Fetcher.ErrorWrapper<undefined>;

export type RemoveOrganizationInvitationVariables = {
  pathParams: RemoveOrganizationInvitationPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchRemoveOrganizationInvitation = (variables: RemoveOrganizationInvitationVariables) =>
  orgsFetch<undefined, RemoveOrganizationInvitationError, undefined, {}, {}, RemoveOrganizationInvitationPathParams>({
    url: '/{realm}/orgs/{orgId}/invitations/{invitationId}',
    method: 'delete',
    ...variables,
  });

export const useRemoveOrganizationInvitation = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, RemoveOrganizationInvitationError, RemoveOrganizationInvitationVariables>,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, RemoveOrganizationInvitationError, RemoveOrganizationInvitationVariables>(
    (variables: RemoveOrganizationInvitationVariables) =>
      fetchRemoveOrganizationInvitation({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetOrganizationRolesPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
};

export type GetOrganizationRolesError = Fetcher.ErrorWrapper<undefined>;

export type GetOrganizationRolesResponse = Schemas.OrganizationRoleRepresentation[];

export type GetOrganizationRolesVariables = {
  pathParams: GetOrganizationRolesPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchGetOrganizationRoles = (variables: GetOrganizationRolesVariables) =>
  orgsFetch<GetOrganizationRolesResponse, GetOrganizationRolesError, undefined, {}, {}, GetOrganizationRolesPathParams>(
    { url: '/{realm}/orgs/{orgId}/roles', method: 'get', ...variables }
  );

export const useGetOrganizationRoles = (
  variables: GetOrganizationRolesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetOrganizationRolesResponse, GetOrganizationRolesError, GetOrganizationRolesResponse>,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<GetOrganizationRolesResponse, GetOrganizationRolesError, GetOrganizationRolesResponse>(
    queryKeyFn({ path: '/{realm}/orgs/{orgId}/roles', operationId: 'getOrganizationRoles', variables }),
    () => fetchGetOrganizationRoles({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type CreateOrganizationRolePathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
};

export type CreateOrganizationRoleError = Fetcher.ErrorWrapper<undefined>;

export type CreateOrganizationRoleVariables = {
  body?: Schemas.OrganizationRoleRepresentation;
  pathParams: CreateOrganizationRolePathParams;
} & OrgsContext['fetcherOptions'];

export const fetchCreateOrganizationRole = (variables: CreateOrganizationRoleVariables) =>
  orgsFetch<
    undefined,
    CreateOrganizationRoleError,
    Schemas.OrganizationRoleRepresentation,
    {},
    {},
    CreateOrganizationRolePathParams
  >({ url: '/{realm}/orgs/{orgId}/roles', method: 'post', ...variables });

export const useCreateOrganizationRole = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, CreateOrganizationRoleError, CreateOrganizationRoleVariables>,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, CreateOrganizationRoleError, CreateOrganizationRoleVariables>(
    (variables: CreateOrganizationRoleVariables) => fetchCreateOrganizationRole({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetOrganizationRolePathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  /*
   * organization role name
   */
  name: string;
};

export type GetOrganizationRoleError = Fetcher.ErrorWrapper<undefined>;

export type GetOrganizationRoleVariables = {
  pathParams: GetOrganizationRolePathParams;
} & OrgsContext['fetcherOptions'];

export const fetchGetOrganizationRole = (variables: GetOrganizationRoleVariables) =>
  orgsFetch<
    Schemas.OrganizationRoleRepresentation,
    GetOrganizationRoleError,
    undefined,
    {},
    {},
    GetOrganizationRolePathParams
  >({ url: '/{realm}/orgs/{orgId}/roles/{name}', method: 'get', ...variables });

export const useGetOrganizationRole = (
  variables: GetOrganizationRoleVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.OrganizationRoleRepresentation,
      GetOrganizationRoleError,
      Schemas.OrganizationRoleRepresentation
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<
    Schemas.OrganizationRoleRepresentation,
    GetOrganizationRoleError,
    Schemas.OrganizationRoleRepresentation
  >(
    queryKeyFn({ path: '/{realm}/orgs/{orgId}/roles/{name}', operationId: 'getOrganizationRole', variables }),
    () => fetchGetOrganizationRole({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type UpdateOrganizationRolePathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  /*
   * organization role name
   */
  name: string;
};

export type UpdateOrganizationRoleError = Fetcher.ErrorWrapper<undefined>;

export type UpdateOrganizationRoleVariables = {
  body?: Schemas.OrganizationRoleRepresentation;
  pathParams: UpdateOrganizationRolePathParams;
} & OrgsContext['fetcherOptions'];

export const fetchUpdateOrganizationRole = (variables: UpdateOrganizationRoleVariables) =>
  orgsFetch<
    undefined,
    UpdateOrganizationRoleError,
    Schemas.OrganizationRoleRepresentation,
    {},
    {},
    UpdateOrganizationRolePathParams
  >({ url: '/{realm}/orgs/{orgId}/roles/{name}', method: 'put', ...variables });

export const useUpdateOrganizationRole = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, UpdateOrganizationRoleError, UpdateOrganizationRoleVariables>,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, UpdateOrganizationRoleError, UpdateOrganizationRoleVariables>(
    (variables: UpdateOrganizationRoleVariables) => fetchUpdateOrganizationRole({ ...fetcherOptions, ...variables }),
    options
  );
};

export type DeleteOrganizationRolePathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  /*
   * organization role name
   */
  name: string;
};

export type DeleteOrganizationRoleError = Fetcher.ErrorWrapper<undefined>;

export type DeleteOrganizationRoleVariables = {
  pathParams: DeleteOrganizationRolePathParams;
} & OrgsContext['fetcherOptions'];

export const fetchDeleteOrganizationRole = (variables: DeleteOrganizationRoleVariables) =>
  orgsFetch<undefined, DeleteOrganizationRoleError, undefined, {}, {}, DeleteOrganizationRolePathParams>({
    url: '/{realm}/orgs/{orgId}/roles/{name}',
    method: 'delete',
    ...variables,
  });

export const useDeleteOrganizationRole = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, DeleteOrganizationRoleError, DeleteOrganizationRoleVariables>,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, DeleteOrganizationRoleError, DeleteOrganizationRoleVariables>(
    (variables: DeleteOrganizationRoleVariables) => fetchDeleteOrganizationRole({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetUserOrganizationRolesPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  /*
   * organization role name
   */
  name: string;
};

export type GetUserOrganizationRolesError = Fetcher.ErrorWrapper<undefined>;

export type GetUserOrganizationRolesResponse = Schemas.UserRepresentation[];

export type GetUserOrganizationRolesVariables = {
  pathParams: GetUserOrganizationRolesPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchGetUserOrganizationRoles = (variables: GetUserOrganizationRolesVariables) =>
  orgsFetch<
    GetUserOrganizationRolesResponse,
    GetUserOrganizationRolesError,
    undefined,
    {},
    {},
    GetUserOrganizationRolesPathParams
  >({ url: '/{realm}/orgs/{orgId}/roles/{name}/users', method: 'get', ...variables });

export const useGetUserOrganizationRoles = (
  variables: GetUserOrganizationRolesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      GetUserOrganizationRolesResponse,
      GetUserOrganizationRolesError,
      GetUserOrganizationRolesResponse
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<
    GetUserOrganizationRolesResponse,
    GetUserOrganizationRolesError,
    GetUserOrganizationRolesResponse
  >(
    queryKeyFn({
      path: '/{realm}/orgs/{orgId}/roles/{name}/users',
      operationId: 'getUserOrganizationRoles',
      variables,
    }),
    () => fetchGetUserOrganizationRoles({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type CheckUserOrganizationRolePathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  /*
   * organization role name
   */
  name: string;
  /*
   * user id
   */
  userId: string;
};

export type CheckUserOrganizationRoleError = Fetcher.ErrorWrapper<undefined>;

export type CheckUserOrganizationRoleVariables = {
  pathParams: CheckUserOrganizationRolePathParams;
} & OrgsContext['fetcherOptions'];

export const fetchCheckUserOrganizationRole = (variables: CheckUserOrganizationRoleVariables) =>
  orgsFetch<undefined, CheckUserOrganizationRoleError, undefined, {}, {}, CheckUserOrganizationRolePathParams>({
    url: '/{realm}/orgs/{orgId}/roles/{name}/users/{userId}',
    method: 'get',
    ...variables,
  });

export const useCheckUserOrganizationRole = (
  variables: CheckUserOrganizationRoleVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<undefined, CheckUserOrganizationRoleError, undefined>,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<undefined, CheckUserOrganizationRoleError, undefined>(
    queryKeyFn({
      path: '/{realm}/orgs/{orgId}/roles/{name}/users/{userId}',
      operationId: 'checkUserOrganizationRole',
      variables,
    }),
    () => fetchCheckUserOrganizationRole({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type GrantUserOrganizationRolePathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  /*
   * organization role name
   */
  name: string;
  /*
   * user id
   */
  userId: string;
};

export type GrantUserOrganizationRoleError = Fetcher.ErrorWrapper<undefined>;

export type GrantUserOrganizationRoleVariables = {
  pathParams: GrantUserOrganizationRolePathParams;
} & OrgsContext['fetcherOptions'];

/**
 * Grant the specified user to the specified organization role
 */
export const fetchGrantUserOrganizationRole = (variables: GrantUserOrganizationRoleVariables) =>
  orgsFetch<undefined, GrantUserOrganizationRoleError, undefined, {}, {}, GrantUserOrganizationRolePathParams>({
    url: '/{realm}/orgs/{orgId}/roles/{name}/users/{userId}',
    method: 'put',
    ...variables,
  });

/**
 * Grant the specified user to the specified organization role
 */
export const useGrantUserOrganizationRole = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, GrantUserOrganizationRoleError, GrantUserOrganizationRoleVariables>,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, GrantUserOrganizationRoleError, GrantUserOrganizationRoleVariables>(
    (variables: GrantUserOrganizationRoleVariables) =>
      fetchGrantUserOrganizationRole({ ...fetcherOptions, ...variables }),
    options
  );
};

export type RevokeUserOrganizationRolePathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  /*
   * organization role name
   */
  name: string;
  /*
   * user id
   */
  userId: string;
};

export type RevokeUserOrganizationRoleError = Fetcher.ErrorWrapper<undefined>;

export type RevokeUserOrganizationRoleVariables = {
  pathParams: RevokeUserOrganizationRolePathParams;
} & OrgsContext['fetcherOptions'];

/**
 * Revoke the specified organization role from the specified user
 */
export const fetchRevokeUserOrganizationRole = (variables: RevokeUserOrganizationRoleVariables) =>
  orgsFetch<undefined, RevokeUserOrganizationRoleError, undefined, {}, {}, RevokeUserOrganizationRolePathParams>({
    url: '/{realm}/orgs/{orgId}/roles/{name}/users/{userId}',
    method: 'delete',
    ...variables,
  });

/**
 * Revoke the specified organization role from the specified user
 */
export const useRevokeUserOrganizationRole = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, RevokeUserOrganizationRoleError, RevokeUserOrganizationRoleVariables>,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, RevokeUserOrganizationRoleError, RevokeUserOrganizationRoleVariables>(
    (variables: RevokeUserOrganizationRoleVariables) =>
      fetchRevokeUserOrganizationRole({ ...fetcherOptions, ...variables }),
    options
  );
};

export type ImportIdpJsonPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
};

export type ImportIdpJsonError = Fetcher.ErrorWrapper<undefined>;

export type ImportIdpJsonResponse = {
  [key: string]: any;
};

export type ImportIdpJsonVariables = {
  pathParams: ImportIdpJsonPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchImportIdpJson = (variables: ImportIdpJsonVariables) =>
  orgsFetch<ImportIdpJsonResponse, ImportIdpJsonError, undefined, {}, {}, ImportIdpJsonPathParams>({
    url: '/{realm}/orgs/{orgId}/idps/import-config',
    method: 'post',
    ...variables,
  });

export const useImportIdpJson = (
  options?: Omit<
    reactQuery.UseMutationOptions<ImportIdpJsonResponse, ImportIdpJsonError, ImportIdpJsonVariables>,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<ImportIdpJsonResponse, ImportIdpJsonError, ImportIdpJsonVariables>(
    (variables: ImportIdpJsonVariables) => fetchImportIdpJson({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetIdpsPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
};

export type GetIdpsError = Fetcher.ErrorWrapper<undefined>;

export type GetIdpsResponse = Schemas.IdentityProviderRepresentation[];

export type GetIdpsVariables = {
  pathParams: GetIdpsPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchGetIdps = (variables: GetIdpsVariables) =>
  orgsFetch<GetIdpsResponse, GetIdpsError, undefined, {}, {}, GetIdpsPathParams>({
    url: '/{realm}/orgs/{orgId}/idps',
    method: 'get',
    ...variables,
  });

export const useGetIdps = (
  variables: GetIdpsVariables,
  options?: Omit<reactQuery.UseQueryOptions<GetIdpsResponse, GetIdpsError, GetIdpsResponse>, 'queryKey' | 'queryFn'>
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<GetIdpsResponse, GetIdpsError, GetIdpsResponse>(
    queryKeyFn({ path: '/{realm}/orgs/{orgId}/idps', operationId: 'getIdps', variables }),
    () => fetchGetIdps({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type CreateIdpPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
};

export type CreateIdpError = Fetcher.ErrorWrapper<undefined>;

export type CreateIdpVariables = {
  body?: Schemas.IdentityProviderRepresentation;
  pathParams: CreateIdpPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchCreateIdp = (variables: CreateIdpVariables) =>
  orgsFetch<undefined, CreateIdpError, Schemas.IdentityProviderRepresentation, {}, {}, CreateIdpPathParams>({
    url: '/{realm}/orgs/{orgId}/idps',
    method: 'post',
    ...variables,
  });

export const useCreateIdp = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, CreateIdpError, CreateIdpVariables>, 'mutationFn'>
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, CreateIdpError, CreateIdpVariables>(
    (variables: CreateIdpVariables) => fetchCreateIdp({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetIdpPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  /*
   * Identity Provider alias
   */
  alias: string;
};

export type GetIdpError = Fetcher.ErrorWrapper<undefined>;

export type GetIdpVariables = {
  pathParams: GetIdpPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchGetIdp = (variables: GetIdpVariables) =>
  orgsFetch<Schemas.IdentityProviderRepresentation, GetIdpError, undefined, {}, {}, GetIdpPathParams>({
    url: '/{realm}/orgs/{orgId}/idps/{alias}',
    method: 'get',
    ...variables,
  });

export const useGetIdp = (
  variables: GetIdpVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.IdentityProviderRepresentation,
      GetIdpError,
      Schemas.IdentityProviderRepresentation
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<
    Schemas.IdentityProviderRepresentation,
    GetIdpError,
    Schemas.IdentityProviderRepresentation
  >(
    queryKeyFn({ path: '/{realm}/orgs/{orgId}/idps/{alias}', operationId: 'getIdp', variables }),
    () => fetchGetIdp({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type UpdateIdpPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  /*
   * Identity Provider alias
   */
  alias: string;
};

export type UpdateIdpError = Fetcher.ErrorWrapper<undefined>;

export type UpdateIdpVariables = {
  body?: Schemas.IdentityProviderRepresentation;
  pathParams: UpdateIdpPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchUpdateIdp = (variables: UpdateIdpVariables) =>
  orgsFetch<undefined, UpdateIdpError, Schemas.IdentityProviderRepresentation, {}, {}, UpdateIdpPathParams>({
    url: '/{realm}/orgs/{orgId}/idps/{alias}',
    method: 'put',
    ...variables,
  });

export const useUpdateIdp = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, UpdateIdpError, UpdateIdpVariables>, 'mutationFn'>
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, UpdateIdpError, UpdateIdpVariables>(
    (variables: UpdateIdpVariables) => fetchUpdateIdp({ ...fetcherOptions, ...variables }),
    options
  );
};

export type DeleteIdpPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  /*
   * Identity Provider alias
   */
  alias: string;
};

export type DeleteIdpError = Fetcher.ErrorWrapper<undefined>;

export type DeleteIdpVariables = {
  pathParams: DeleteIdpPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchDeleteIdp = (variables: DeleteIdpVariables) =>
  orgsFetch<undefined, DeleteIdpError, undefined, {}, {}, DeleteIdpPathParams>({
    url: '/{realm}/orgs/{orgId}/idps/{alias}',
    method: 'delete',
    ...variables,
  });

export const useDeleteIdp = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, DeleteIdpError, DeleteIdpVariables>, 'mutationFn'>
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, DeleteIdpError, DeleteIdpVariables>(
    (variables: DeleteIdpVariables) => fetchDeleteIdp({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetIdpMappersPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  alias: string;
};

export type GetIdpMappersError = Fetcher.ErrorWrapper<undefined>;

export type GetIdpMappersResponse = Schemas.IdentityProviderMapperRepresentation[];

export type GetIdpMappersVariables = {
  pathParams: GetIdpMappersPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchGetIdpMappers = (variables: GetIdpMappersVariables) =>
  orgsFetch<GetIdpMappersResponse, GetIdpMappersError, undefined, {}, {}, GetIdpMappersPathParams>({
    url: '/{realm}/orgs/{orgId}/idps/{alias}/mappers',
    method: 'get',
    ...variables,
  });

export const useGetIdpMappers = (
  variables: GetIdpMappersVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetIdpMappersResponse, GetIdpMappersError, GetIdpMappersResponse>,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<GetIdpMappersResponse, GetIdpMappersError, GetIdpMappersResponse>(
    queryKeyFn({ path: '/{realm}/orgs/{orgId}/idps/{alias}/mappers', operationId: 'getIdpMappers', variables }),
    () => fetchGetIdpMappers({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type AddIdpMapperPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  alias: string;
};

export type AddIdpMapperError = Fetcher.ErrorWrapper<undefined>;

export type AddIdpMapperVariables = {
  body?: Schemas.IdentityProviderMapperRepresentation;
  pathParams: AddIdpMapperPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchAddIdpMapper = (variables: AddIdpMapperVariables) =>
  orgsFetch<undefined, AddIdpMapperError, Schemas.IdentityProviderMapperRepresentation, {}, {}, AddIdpMapperPathParams>(
    { url: '/{realm}/orgs/{orgId}/idps/{alias}/mappers', method: 'post', ...variables }
  );

export const useAddIdpMapper = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, AddIdpMapperError, AddIdpMapperVariables>, 'mutationFn'>
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, AddIdpMapperError, AddIdpMapperVariables>(
    (variables: AddIdpMapperVariables) => fetchAddIdpMapper({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetIdpMapperPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  alias: string;
  /*
   * Mapper id
   */
  id: string;
};

export type GetIdpMapperError = Fetcher.ErrorWrapper<undefined>;

export type GetIdpMapperVariables = {
  pathParams: GetIdpMapperPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchGetIdpMapper = (variables: GetIdpMapperVariables) =>
  orgsFetch<Schemas.IdentityProviderMapperRepresentation, GetIdpMapperError, undefined, {}, {}, GetIdpMapperPathParams>(
    { url: '/{realm}/orgs/{orgId}/idps/{alias}/mappers/{id}', method: 'get', ...variables }
  );

export const useGetIdpMapper = (
  variables: GetIdpMapperVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.IdentityProviderMapperRepresentation,
      GetIdpMapperError,
      Schemas.IdentityProviderMapperRepresentation
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<
    Schemas.IdentityProviderMapperRepresentation,
    GetIdpMapperError,
    Schemas.IdentityProviderMapperRepresentation
  >(
    queryKeyFn({ path: '/{realm}/orgs/{orgId}/idps/{alias}/mappers/{id}', operationId: 'getIdpMapper', variables }),
    () => fetchGetIdpMapper({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type UpdateIdpMapperPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  alias: string;
  /*
   * Mapper id
   */
  id: string;
};

export type UpdateIdpMapperError = Fetcher.ErrorWrapper<undefined>;

export type UpdateIdpMapperVariables = {
  body?: Schemas.IdentityProviderMapperRepresentation;
  pathParams: UpdateIdpMapperPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchUpdateIdpMapper = (variables: UpdateIdpMapperVariables) =>
  orgsFetch<
    undefined,
    UpdateIdpMapperError,
    Schemas.IdentityProviderMapperRepresentation,
    {},
    {},
    UpdateIdpMapperPathParams
  >({ url: '/{realm}/orgs/{orgId}/idps/{alias}/mappers/{id}', method: 'put', ...variables });

export const useUpdateIdpMapper = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, UpdateIdpMapperError, UpdateIdpMapperVariables>, 'mutationFn'>
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, UpdateIdpMapperError, UpdateIdpMapperVariables>(
    (variables: UpdateIdpMapperVariables) => fetchUpdateIdpMapper({ ...fetcherOptions, ...variables }),
    options
  );
};

export type DeleteIdpMapperPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * organization id
   */
  orgId: string;
  alias: string;
  /*
   * Mapper id
   */
  id: string;
};

export type DeleteIdpMapperError = Fetcher.ErrorWrapper<undefined>;

export type DeleteIdpMapperVariables = {
  pathParams: DeleteIdpMapperPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchDeleteIdpMapper = (variables: DeleteIdpMapperVariables) =>
  orgsFetch<undefined, DeleteIdpMapperError, undefined, {}, {}, DeleteIdpMapperPathParams>({
    url: '/{realm}/orgs/{orgId}/idps/{alias}/mappers/{id}',
    method: 'delete',
    ...variables,
  });

export const useDeleteIdpMapper = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, DeleteIdpMapperError, DeleteIdpMapperVariables>, 'mutationFn'>
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, DeleteIdpMapperError, DeleteIdpMapperVariables>(
    (variables: DeleteIdpMapperVariables) => fetchDeleteIdpMapper({ ...fetcherOptions, ...variables }),
    options
  );
};

export type CreateEventPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
};

export type CreateEventError = Fetcher.ErrorWrapper<undefined>;

export type CreateEventVariables = {
  body?: Schemas.EventRepresentation;
  pathParams: CreateEventPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchCreateEvent = (variables: CreateEventVariables) =>
  orgsFetch<undefined, CreateEventError, Schemas.EventRepresentation, {}, {}, CreateEventPathParams>({
    url: '/{realm}/events',
    method: 'post',
    ...variables,
  });

export const useCreateEvent = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, CreateEventError, CreateEventVariables>, 'mutationFn'>
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, CreateEventError, CreateEventVariables>(
    (variables: CreateEventVariables) => fetchCreateEvent({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetRealmAttributesPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
};

export type GetRealmAttributesError = Fetcher.ErrorWrapper<undefined>;

export type GetRealmAttributesResponse = Schemas.KeyedRealmAttributeRepresentation[];

export type GetRealmAttributesVariables = {
  pathParams: GetRealmAttributesPathParams;
} & OrgsContext['fetcherOptions'];

/**
 * Get a list of attributes for this realm
 */
export const fetchGetRealmAttributes = (variables: GetRealmAttributesVariables) =>
  orgsFetch<GetRealmAttributesResponse, GetRealmAttributesError, undefined, {}, {}, GetRealmAttributesPathParams>({
    url: '/{realm}/attributes',
    method: 'get',
    ...variables,
  });

/**
 * Get a list of attributes for this realm
 */
export const useGetRealmAttributes = (
  variables: GetRealmAttributesVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetRealmAttributesResponse, GetRealmAttributesError, GetRealmAttributesResponse>,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<GetRealmAttributesResponse, GetRealmAttributesError, GetRealmAttributesResponse>(
    queryKeyFn({ path: '/{realm}/attributes', operationId: 'getRealmAttributes', variables }),
    () => fetchGetRealmAttributes({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type CreateRealmAttributePathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
};

export type CreateRealmAttributeError = Fetcher.ErrorWrapper<undefined>;

export type CreateRealmAttributeVariables = {
  body?: Schemas.RealmAttributeRepresentation;
  pathParams: CreateRealmAttributePathParams;
} & OrgsContext['fetcherOptions'];

export const fetchCreateRealmAttribute = (variables: CreateRealmAttributeVariables) =>
  orgsFetch<
    undefined,
    CreateRealmAttributeError,
    Schemas.RealmAttributeRepresentation,
    {},
    {},
    CreateRealmAttributePathParams
  >({ url: '/{realm}/attributes', method: 'post', ...variables });

export const useCreateRealmAttribute = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, CreateRealmAttributeError, CreateRealmAttributeVariables>,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, CreateRealmAttributeError, CreateRealmAttributeVariables>(
    (variables: CreateRealmAttributeVariables) => fetchCreateRealmAttribute({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetRealmAttributeByKeyPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * attribute key
   */
  attributeKey: string;
};

export type GetRealmAttributeByKeyError = Fetcher.ErrorWrapper<undefined>;

export type GetRealmAttributeByKeyVariables = {
  pathParams: GetRealmAttributeByKeyPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchGetRealmAttributeByKey = (variables: GetRealmAttributeByKeyVariables) =>
  orgsFetch<
    Schemas.RealmAttributeRepresentation,
    GetRealmAttributeByKeyError,
    undefined,
    {},
    {},
    GetRealmAttributeByKeyPathParams
  >({ url: '/{realm}/attributes/{attributeKey}', method: 'get', ...variables });

export const useGetRealmAttributeByKey = (
  variables: GetRealmAttributeByKeyVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<
      Schemas.RealmAttributeRepresentation,
      GetRealmAttributeByKeyError,
      Schemas.RealmAttributeRepresentation
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<
    Schemas.RealmAttributeRepresentation,
    GetRealmAttributeByKeyError,
    Schemas.RealmAttributeRepresentation
  >(
    queryKeyFn({ path: '/{realm}/attributes/{attributeKey}', operationId: 'getRealmAttributeByKey', variables }),
    () => fetchGetRealmAttributeByKey({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type UpdateRealmAttributeByKeyPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * attribute key
   */
  attributeKey: string;
};

export type UpdateRealmAttributeByKeyError = Fetcher.ErrorWrapper<undefined>;

export type UpdateRealmAttributeByKeyVariables = {
  body?: Schemas.RealmAttributeRepresentation;
  pathParams: UpdateRealmAttributeByKeyPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchUpdateRealmAttributeByKey = (variables: UpdateRealmAttributeByKeyVariables) =>
  orgsFetch<
    undefined,
    UpdateRealmAttributeByKeyError,
    Schemas.RealmAttributeRepresentation,
    {},
    {},
    UpdateRealmAttributeByKeyPathParams
  >({ url: '/{realm}/attributes/{attributeKey}', method: 'put', ...variables });

export const useUpdateRealmAttributeByKey = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, UpdateRealmAttributeByKeyError, UpdateRealmAttributeByKeyVariables>,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, UpdateRealmAttributeByKeyError, UpdateRealmAttributeByKeyVariables>(
    (variables: UpdateRealmAttributeByKeyVariables) =>
      fetchUpdateRealmAttributeByKey({ ...fetcherOptions, ...variables }),
    options
  );
};

export type DeleteRealmAttributePathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * attribute key
   */
  attributeKey: string;
};

export type DeleteRealmAttributeError = Fetcher.ErrorWrapper<undefined>;

export type DeleteRealmAttributeVariables = {
  pathParams: DeleteRealmAttributePathParams;
} & OrgsContext['fetcherOptions'];

export const fetchDeleteRealmAttribute = (variables: DeleteRealmAttributeVariables) =>
  orgsFetch<undefined, DeleteRealmAttributeError, undefined, {}, {}, DeleteRealmAttributePathParams>({
    url: '/{realm}/attributes/{attributeKey}',
    method: 'delete',
    ...variables,
  });

export const useDeleteRealmAttribute = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, DeleteRealmAttributeError, DeleteRealmAttributeVariables>,
    'mutationFn'
  >
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, DeleteRealmAttributeError, DeleteRealmAttributeVariables>(
    (variables: DeleteRealmAttributeVariables) => fetchDeleteRealmAttribute({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetWebhooksPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
};

export type GetWebhooksError = Fetcher.ErrorWrapper<undefined>;

export type GetWebhooksResponse = Schemas.WebhookRepresentation[];

export type GetWebhooksVariables = {
  pathParams: GetWebhooksPathParams;
} & OrgsContext['fetcherOptions'];

/**
 * Get a list of webhooks for this realm
 */
export const fetchGetWebhooks = (variables: GetWebhooksVariables) =>
  orgsFetch<GetWebhooksResponse, GetWebhooksError, undefined, {}, {}, GetWebhooksPathParams>({
    url: '/{realm}/webhooks',
    method: 'get',
    ...variables,
  });

/**
 * Get a list of webhooks for this realm
 */
export const useGetWebhooks = (
  variables: GetWebhooksVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<GetWebhooksResponse, GetWebhooksError, GetWebhooksResponse>,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<GetWebhooksResponse, GetWebhooksError, GetWebhooksResponse>(
    queryKeyFn({ path: '/{realm}/webhooks', operationId: 'getWebhooks', variables }),
    () => fetchGetWebhooks({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type CreateWebhookPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
};

export type CreateWebhookError = Fetcher.ErrorWrapper<undefined>;

export type CreateWebhookVariables = {
  body?: Schemas.WebhookRepresentation;
  pathParams: CreateWebhookPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchCreateWebhook = (variables: CreateWebhookVariables) =>
  orgsFetch<undefined, CreateWebhookError, Schemas.WebhookRepresentation, {}, {}, CreateWebhookPathParams>({
    url: '/{realm}/webhooks',
    method: 'post',
    ...variables,
  });

export const useCreateWebhook = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, CreateWebhookError, CreateWebhookVariables>, 'mutationFn'>
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, CreateWebhookError, CreateWebhookVariables>(
    (variables: CreateWebhookVariables) => fetchCreateWebhook({ ...fetcherOptions, ...variables }),
    options
  );
};

export type GetWebhookByIdPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * webhook id
   */
  webhookId: string;
};

export type GetWebhookByIdError = Fetcher.ErrorWrapper<undefined>;

export type GetWebhookByIdVariables = {
  pathParams: GetWebhookByIdPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchGetWebhookById = (variables: GetWebhookByIdVariables) =>
  orgsFetch<Schemas.WebhookRepresentation, GetWebhookByIdError, undefined, {}, {}, GetWebhookByIdPathParams>({
    url: '/{realm}/webhooks/{webhookId}',
    method: 'get',
    ...variables,
  });

export const useGetWebhookById = (
  variables: GetWebhookByIdVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.WebhookRepresentation, GetWebhookByIdError, Schemas.WebhookRepresentation>,
    'queryKey' | 'queryFn'
  >
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useOrgsContext(options);
  return reactQuery.useQuery<Schemas.WebhookRepresentation, GetWebhookByIdError, Schemas.WebhookRepresentation>(
    queryKeyFn({ path: '/{realm}/webhooks/{webhookId}', operationId: 'getWebhookById', variables }),
    () => fetchGetWebhookById({ ...fetcherOptions, ...variables }),
    {
      ...options,
      ...queryOptions,
    }
  );
};

export type UpdateWebhookPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * webhook id
   */
  webhookId: string;
};

export type UpdateWebhookError = Fetcher.ErrorWrapper<undefined>;

export type UpdateWebhookVariables = {
  body?: Schemas.WebhookRepresentation;
  pathParams: UpdateWebhookPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchUpdateWebhook = (variables: UpdateWebhookVariables) =>
  orgsFetch<undefined, UpdateWebhookError, Schemas.WebhookRepresentation, {}, {}, UpdateWebhookPathParams>({
    url: '/{realm}/webhooks/{webhookId}',
    method: 'put',
    ...variables,
  });

export const useUpdateWebhook = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, UpdateWebhookError, UpdateWebhookVariables>, 'mutationFn'>
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, UpdateWebhookError, UpdateWebhookVariables>(
    (variables: UpdateWebhookVariables) => fetchUpdateWebhook({ ...fetcherOptions, ...variables }),
    options
  );
};

export type DeleteWebhookPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
  /*
   * webhook id
   */
  webhookId: string;
};

export type DeleteWebhookError = Fetcher.ErrorWrapper<undefined>;

export type DeleteWebhookVariables = {
  pathParams: DeleteWebhookPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchDeleteWebhook = (variables: DeleteWebhookVariables) =>
  orgsFetch<undefined, DeleteWebhookError, undefined, {}, {}, DeleteWebhookPathParams>({
    url: '/{realm}/webhooks/{webhookId}',
    method: 'delete',
    ...variables,
  });

export const useDeleteWebhook = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, DeleteWebhookError, DeleteWebhookVariables>, 'mutationFn'>
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, DeleteWebhookError, DeleteWebhookVariables>(
    (variables: DeleteWebhookVariables) => fetchDeleteWebhook({ ...fetcherOptions, ...variables }),
    options
  );
};

export type CreateMagicLinkPathParams = {
  /*
   * realm name (not id!)
   */
  realm: string;
};

export type CreateMagicLinkError = Fetcher.ErrorWrapper<undefined>;

export type CreateMagicLinkVariables = {
  body: Schemas.MagicLinkRepresentation;
  pathParams: CreateMagicLinkPathParams;
} & OrgsContext['fetcherOptions'];

export const fetchCreateMagicLink = (variables: CreateMagicLinkVariables) =>
  orgsFetch<undefined, CreateMagicLinkError, Schemas.MagicLinkRepresentation, {}, {}, CreateMagicLinkPathParams>({
    url: '/{realm}/magic-link',
    method: 'post',
    ...variables,
  });

export const useCreateMagicLink = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, CreateMagicLinkError, CreateMagicLinkVariables>, 'mutationFn'>
) => {
  const { fetcherOptions } = useOrgsContext();
  return reactQuery.useMutation<undefined, CreateMagicLinkError, CreateMagicLinkVariables>(
    (variables: CreateMagicLinkVariables) => fetchCreateMagicLink({ ...fetcherOptions, ...variables }),
    options
  );
};

export type QueryOperation =
  | {
      path: '/{realm}/orgs';
      operationId: 'getOrganizations';
      variables: GetOrganizationsVariables;
    }
  | {
      path: '/{realm}/orgs/count';
      operationId: 'getOrganizationsCount';
      variables: GetOrganizationsCountVariables;
    }
  | {
      path: '/{realm}/orgs/me';
      operationId: 'getMe';
      variables: GetMeVariables;
    }
  | {
      path: '/{realm}/orgs/{orgId}';
      operationId: 'getOrganizationById';
      variables: GetOrganizationByIdVariables;
    }
  | {
      path: '/{realm}/orgs/{orgId}/members';
      operationId: 'getOrganizationMemberships';
      variables: GetOrganizationMembershipsVariables;
    }
  | {
      path: '/{realm}/orgs/{orgId}/members/count';
      operationId: 'getOrganizationMembershipsCount';
      variables: GetOrganizationMembershipsCountVariables;
    }
  | {
      path: '/{realm}/orgs/{orgId}/domains';
      operationId: 'getOrganizationDomains';
      variables: GetOrganizationDomainsVariables;
    }
  | {
      path: '/{realm}/orgs/{orgId}/domains/{domainName}';
      operationId: 'getOrganizationDomain';
      variables: GetOrganizationDomainVariables;
    }
  | {
      path: '/{realm}/orgs/{orgId}/members/{userId}';
      operationId: 'checkOrganizationMembership';
      variables: CheckOrganizationMembershipVariables;
    }
  | {
      path: '/{realm}/orgs/{orgId}/invitations';
      operationId: 'getOrganizationInvitations';
      variables: GetOrganizationInvitationsVariables;
    }
  | {
      path: '/{realm}/orgs/{orgId}/roles';
      operationId: 'getOrganizationRoles';
      variables: GetOrganizationRolesVariables;
    }
  | {
      path: '/{realm}/orgs/{orgId}/roles/{name}';
      operationId: 'getOrganizationRole';
      variables: GetOrganizationRoleVariables;
    }
  | {
      path: '/{realm}/orgs/{orgId}/roles/{name}/users';
      operationId: 'getUserOrganizationRoles';
      variables: GetUserOrganizationRolesVariables;
    }
  | {
      path: '/{realm}/orgs/{orgId}/roles/{name}/users/{userId}';
      operationId: 'checkUserOrganizationRole';
      variables: CheckUserOrganizationRoleVariables;
    }
  | {
      path: '/{realm}/orgs/{orgId}/idps';
      operationId: 'getIdps';
      variables: GetIdpsVariables;
    }
  | {
      path: '/{realm}/orgs/{orgId}/idps/{alias}';
      operationId: 'getIdp';
      variables: GetIdpVariables;
    }
  | {
      path: '/{realm}/orgs/{orgId}/idps/{alias}/mappers';
      operationId: 'getIdpMappers';
      variables: GetIdpMappersVariables;
    }
  | {
      path: '/{realm}/orgs/{orgId}/idps/{alias}/mappers/{id}';
      operationId: 'getIdpMapper';
      variables: GetIdpMapperVariables;
    }
  | {
      path: '/{realm}/attributes';
      operationId: 'getRealmAttributes';
      variables: GetRealmAttributesVariables;
    }
  | {
      path: '/{realm}/attributes/{attributeKey}';
      operationId: 'getRealmAttributeByKey';
      variables: GetRealmAttributeByKeyVariables;
    }
  | {
      path: '/{realm}/webhooks';
      operationId: 'getWebhooks';
      variables: GetWebhooksVariables;
    }
  | {
      path: '/{realm}/webhooks/{webhookId}';
      operationId: 'getWebhookById';
      variables: GetWebhookByIdVariables;
    };
